import { useMetaDescriptionSyllabus } from '@/components/SyllabusViewNew'
import { StageOrYearContentAccordion } from '@/components/syllabus-tabs/Content'
import { STAGE_YEARS, YEARS } from '@/constants'
import { useQueryStringByRouterOrWindow } from '@/hooks/useQueryStringByRouterOrWindow'
import {
	Course,
	Focusarea,
	Optionslist,
	Syllabus,
	Weblinkext,
	Weblinkvideo,
} from '@/kontent/content-types'
import { contentTypes } from '@/kontent/project/contentTypes'
import { taxonomies } from '@/kontent/project/taxonomies'
import { TaxoStageYear } from '@/kontent/taxonomies'
import Content, {
	ContentOrganizerProps,
} from '@/legacy-ported/components/syllabus/Content'
import { getUrlFromFocusArea } from '@/legacy-ported/components/syllabus/ContentSideNav'
import { SYLLABUS_TABS } from '@/legacy-ported/constants'
import { getItemByCodename } from '@/lib/api'
import { getAllItemsByType } from '@/lib/getAllItemsByType'
import { getItemsByCodenames } from '@/lib/getItemsByCodenames'
import { NextPageGetLayout, getTitleWithSuffix } from '@/pages/_app'
import {
	CommonPageProps,
	CustomSyllabusTab,
	TaxoStageWithLifeSkill,
	TaxoStageYearWithLifeSkill,
} from '@/types'
import { FocusareaOrOptionListOrFocusareoptionExtended } from '@/types/customKontentTypes'
import {
	byIContentItemCodename,
	byTaxoCodename,
	filterPreviewableSyllabusesOnly,
	fnExist,
	getCodenameBySlug,
	getLinkedItems,
	getSlugByCodename,
	getSyllabusElements,
	getTaxoCodenamesFromTaxoTerms,
	isIntersect,
	isYes,
	redirectTo404,
	uniquePrimitiveArray,
} from '@/utils'
import { shouldHideTabBasedOnCecContentHide } from '@/utils/cec'
import { cleanJson } from '@/utils/cleanJson'
import {
	convertToFocusareasOrOptionListOrFocusareaoptionsExtended,
	isLifeSkillFocusAreaOrOptionListOrOutcome,
} from '@/utils/focusarea'
import {
	getCodenameBySlugYear,
	getSlugByCodenameYear,
} from '@/utils/slug-year-utils'
import {
	getRedirectPropsForFocusAreaNotMatchSyllabus,
	getStageAndYearCodenamesFromStageOrYearSlug,
	getStaticPropsForRedirectTo404ForNonPreviewedSyllabus,
	hasLifeSkillRelatedSyllabus,
	isLifeSkillSyllabus,
	isStage6Syllabus,
} from '@/utils/syllabus'
import { isFocusarea, isOutcome } from '@/utils/type_predicates'
import type { Elements } from '@kontent-ai/delivery-sdk'
import {
	ElementModels,
	IContentItem,
	IContentItemsContainer,
	Responses,
} from '@kontent-ai/delivery-sdk'
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { useQueryState } from 'next-usequerystate'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { useMemo } from 'react'
import {
	CommonContentTab,
	getCommonSyllabusData,
	getStagesOrYearsBasedOnSyllabusAndDisabledStages,
	getLayout as getTabLayout,
} from '..'

type ContentStageFocusAreaPageResultData = Omit<
	CommonContentTab<FocusareaOrOptionListOrFocusareoptionExtended>,
	'webLinkVideos' | 'webLinkExternals'
> & {
	stageFocusAreas: FocusareaOrOptionListOrFocusareoptionExtended[]
	stageOrYears: ElementModels.TaxonomyTerm<
		TaxoStageWithLifeSkill | TaxoStageYearWithLifeSkill
	>[]
	syllabusCourses: Course[]
	syllabusFocusAreaResponse: Responses.IListContentItemsResponse<Syllabus>
	webLinkVideos: Weblinkvideo[]
	webLinkExternals: Weblinkext[]
}

type ContentStageFocusAreaPageProps = CommonPageProps<
	Syllabus,
	ContentStageFocusAreaPageResultData
> & {
	showExamples?: boolean
}

const getYearOrStageTitle = (stageId, yearId, course) => {
	if (yearId === 'life_skills' || stageId === 'life_skills')
		return 'Life Skills'

	if (yearId) {
		let _yearId = yearId?.split('_')[0] as TaxoStageYear
		let yearExtension
		if (course) {
			yearExtension = course.elements.content_accordion_title.value
		}

		return `Year ${taxonomies.stage_year.terms[_yearId]?.name}${
			yearExtension ? ` ${yearExtension}` : ''
		}`
	}
	if (stageId) {
		return taxonomies.stage.terms[stageId]?.name
	}
	return ''
}

export const QS_SHOW = {
	ADVICE: 'advice',
	EXAMPLE: 'example',
	ACCESS_CONTENT_POINTS: 'accesscontent',
	VIEW_LIFE_SKILLS: 'ls',
	CURRICULUM_CONNECTION: 'cc',
}

export const useShowAndTaScrollQueryString = () => {
	const [qsShow] = useQueryState('show')
	const [qsTaScroll] = useQueryState('ta_scroll')
	const showSplit = (qsShow || '')?.split(',')
	const showTeachingAdvice = showSplit?.includes(QS_SHOW.ADVICE)
	const showExamples = showSplit?.includes(QS_SHOW.EXAMPLE)
	const showAccessContentPoints = showSplit?.includes(
		QS_SHOW.ACCESS_CONTENT_POINTS,
	)
	const taScroll = qsTaScroll !== 'no'
	const showCurriculumConnection = showSplit?.includes(
		QS_SHOW.CURRICULUM_CONNECTION,
	)

	return {
		showTeachingAdvice,
		showExamples,
		showAccessContentPoints,
		taScroll,
		showCurriculumConnection,
	}
}

export const getFocusAreaCanonicalUrl = ({
	focusArea,
	syllabusResponse,
	currentStage,
	currentYear,
}: {
	focusArea: FocusareaOrOptionListOrFocusareoptionExtended

	// Focus area's syllabus
	syllabusResponse: Responses.IViewContentItemResponse<Syllabus>

	// Current stage
	currentStage: TaxoStageWithLifeSkill

	// Current year
	currentYear: TaxoStageYearWithLifeSkill
}) => {
	const isFocusareaLifeSkill =
		isLifeSkillFocusAreaOrOptionListOrOutcome(focusArea)
	let stage = currentStage
	let year = currentYear

	// check whether its syllabus has related life skills syllabus or is life skills syllabus
	let syllabusCodename = getSlugByCodename(
		syllabusResponse.item.system.codename,
	)
	if (isLifeSkillSyllabus(syllabusResponse.item) || isFocusareaLifeSkill) {
		stage = 'life_skills'
		year = 'life_skills'
	}

	if (
		syllabusResponse.item.elements.relatedlifeskillssyllabus.value.length &&
		isFocusareaLifeSkill
	) {
		syllabusCodename = getSlugByCodename(
			syllabusResponse.item.elements.relatedlifeskillssyllabus.value[0],
		)
	}

	const href = getUrlFromFocusArea(focusArea, syllabusCodename, stage, year)

	return href
}

export default function ContentStageFocusAreaPage({
	params,
	data,
}: InferGetStaticPropsType<typeof getStaticProps>) {
	const {
		focusArea: focusAreaOrOptionlist,
		syllabus: syllabusResponse,
		stageFocusAreas,
		assets,
		websiteConfiguration: wcConfig,
		syllabusCourses,
		syllabusFocusAreaResponse,
		webLinkVideos,
		webLinkExternals,
	} = data
	const syllabus = syllabusResponse.item
	const isStage6Syl = isStage6Syllabus(syllabus)
	const isLifeSkillSyl = isLifeSkillSyllabus(syllabus)

	const currentSyllabusCourse = syllabusCourses.find(
		(course) =>
			getSlugByCodenameYear(
				course.elements.stage_years.value[0].codename,
			) +
				'-' +
				course.elements.slug?.value ===
			params.stage,
	)

	const isSyllabusCourse = !!currentSyllabusCourse

	const stageOrYearId = getCodenameBySlugYear(
		params.stage,
	) as TaxoStageWithLifeSkill

	const router = useRouter()
	const { query } = router

	const isParamStage_stage =
		stageOrYearId === 'life_skills' ||
		Object.keys(STAGE_YEARS).includes(stageOrYearId)
	const isParamStage_year =
		stageOrYearId === 'life_skills' ||
		YEARS.map((y) => y.codename).includes(stageOrYearId as TaxoStageYear)

	const hasLifeSkillRelatedSyl = hasLifeSkillRelatedSyllabus(syllabus)

	const isCurrentStage4Or5Or6 =
		stageOrYearId === 'stage_4' ||
		stageOrYearId === 'stage_5' ||
		isStage6Syl

	const {
		showAccessContentPoints,
		showExamples,
		showTeachingAdvice,
		taScroll,
		showCurriculumConnection,
	} = useShowAndTaScrollQueryString()

	const { state: langs } = useQueryStringByRouterOrWindow('langs', '', true)
	const { state: paths } = useQueryStringByRouterOrWindow('paths', '', true)

	const initialState = useMemo<ContentOrganizerProps['initialState']>(() => {
		return {
			teachingSupport: showTeachingAdvice,
			examples: showExamples,
			accessPoints: showAccessContentPoints,
			taScroll,
			focusAreaOption: getCodenameBySlug(
				query.afterStageSlugs?.[1] || params.afterStageSlugs?.[1] || '',
			),
			langs,
			pathways: paths,
			curriculumConnection: showCurriculumConnection,
		}
	}, [
		showAccessContentPoints,
		showExamples,
		showTeachingAdvice,
		taScroll,
		params.afterStageSlugs,
		query,
		langs,
		paths,
		showCurriculumConnection,
	])

	const stageId = useMemo(() => {
		if (isParamStage_stage) {
			return stageOrYearId
		} else {
			if (isStage6Syl) {
				return 'stage_6'
			}
			if (isLifeSkillSyl) {
				return 'life_skills'
			}
		}
		return undefined
	}, [isLifeSkillSyl, isParamStage_stage, isStage6Syl, stageOrYearId])

	const yearId = useMemo<TaxoStageYearWithLifeSkill>(() => {
		if (isParamStage_year || isSyllabusCourse) {
			return stageOrYearId as TaxoStageYearWithLifeSkill
		}
		if (isSyllabusCourse) {
			return stageOrYearId.split('_')[0] as TaxoStageYearWithLifeSkill
		}
		return undefined
	}, [isParamStage_year, stageOrYearId, isSyllabusCourse])

	const stageOrYearTitle = getYearOrStageTitle(
		stageId,
		yearId,
		currentSyllabusCourse,
	)
	const metaDescription = useMetaDescriptionSyllabus({
		syllabus,
		syllabusTab: SYLLABUS_TABS.find((t) => t.id === 'content'),
		stage: stageOrYearTitle,
		focusAreaOrOptionlist: focusAreaOrOptionlist.item,
	})

	const filteredStageFocusAreas = (
		stageFocusAreas as (FocusareaOrOptionListOrFocusareoptionExtended & {
			elements: {
				course: Elements.LinkedItemsElement<Course>
			}
		})[]
	).filter((fa) => {
		if (isSyllabusCourse) {
			return (
				fa.elements.course.value.includes(
					currentSyllabusCourse.system.codename,
				) ||
				fa.elements.syllabus_type__items.value[0].codename ==
					'life_skills'
			)
		}
		return true
	})

	return (
		<>
			<Head>
				<title>
					{getTitleWithSuffix(
						`${syllabus.elements.title.value} - ${stageOrYearTitle} - ${focusAreaOrOptionlist.item.elements.title.value}`,
						wcConfig,
					)}
				</title>
				<meta name="robots" content="noindex,nofollow" />
				<meta name="description" content={metaDescription} />
				<link
					key="canonical"
					rel="canonical"
					href={getFocusAreaCanonicalUrl({
						focusArea: focusAreaOrOptionlist.item,
						syllabusResponse,
						currentStage: stageId,
						currentYear: yearId,
					})}
				/>
			</Head>
			<Content
				linkedItems={Object.assign(
					syllabusResponse.linkedItems,
					focusAreaOrOptionlist.linkedItems,
					syllabusFocusAreaResponse.linkedItems,
				)}
				stages={
					focusAreaOrOptionlist.item.elements.stages__stages
						.value as ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[]
				}
				stageId={stageId}
				yearId={
					isSyllabusCourse
						? currentSyllabusCourse.elements.stage_years.value[0]
								.codename
						: yearId
				}
				supportElementId=""
				syllabus={syllabus}
				focusAreasOrOptionList={filteredStageFocusAreas}
				files={assets}
				hideToggleViewLifeSkills={
					(isStage6Syl && !hasLifeSkillRelatedSyl) ||
					params.stage === 'life-skills' ||
					!isCurrentStage4Or5Or6
				}
				initialState={initialState}
				lifeSkillsInfoForFocusArea={
					syllabus.elements.lifeskills_info_focusareas
				}
				initialStageCodename={stageId}
				initialYearCodename={
					isSyllabusCourse && currentSyllabusCourse
						? currentSyllabusCourse.elements.stage_years.value[0]
								.codename
						: yearId
				}
				focusAreaResponse={focusAreaOrOptionlist}
				currentSyllabusCourse={currentSyllabusCourse}
				currentSyllabusCourseYearId={yearId}
				pageType={
					'Syllabus focus area' +
					(params.afterStageSlugs?.[1] ? ' option' : '')
				}
				syllabusWideWebLinkVideos={webLinkVideos}
				syllabusWideWebLinkExternals={webLinkExternals}
			></Content>
		</>
	)
}

export type CommonSyllabusPathParams = ParsedUrlQuery & {
	learningarea: string
	syllabus: string
	tab?: string
	stage?: string
	afterStageSlugs?: string[]
}

export type ReturnTypeOfGetStaticProps = CommonPageProps<
	Syllabus,
	ContentStageFocusAreaPageResultData,
	CommonSyllabusPathParams
>

// const getLifeSkillStages = (
// 	obj: Focusarea | Optionslist | Focusareaoption,
// 	syllabus: Syllabus,
// 	linkedItems: IContentItemsContainer,
// ) => {
// 	const isStage6Syl = isStage6Syllabus(syllabus)
// 	const isLifeSkillFA = isLifeSkillFocusAreaOrOptionListOrOutcome(obj)
// 	if (!isLifeSkillFA)
// 		return syllabus.elements.stages__stages.value.flatMap(byTaxoCodename)

// 	const relatedMainstreamFAs =
// 		(isFocusarea(obj) || isFocusareaoption(obj)) &&
// 		obj.elements.related_focusareas?.value?.length
// 			? getLinkedItems(obj.elements.related_focusareas, linkedItems)
// 			: [obj]

// 	// if no related mainstream FAs & not stage 6, return 4/5 and life skills
// 	if (!isStage6Syl) {
// 		return ['stage_4', 'stage_5', 'life_skills'] as TaxoStageWithLifeSkill[]
// 	}

// 	return [
// 		'life_skills',
// 		...(relatedMainstreamFAs?.flatMap((fa) => {
// 			const faYearsOrStages = (
// 				isStage6Syl
// 					? fa.elements.stages__stage_years.value
// 					: fa.elements.stages__stages.value
// 			).flatMap(byTaxoCodename)

// 			return faYearsOrStages
// 		}) || []),
// 	] as TaxoStageWithLifeSkill[]
// }

export const getStaticPaths: GetStaticPaths = async () => {
	// const syllabusResponse = await getAllItemsByType<Syllabus>({
	// 	type: contentTypes.syllabus.codename,
	// 	depth: 2,
	// 	preview: false,
	// 	elementsParameter: [
	// 		contentTypes.syllabus.elements.focus_areas.codename,
	// 		contentTypes.syllabus.elements.key_learning_area__items.codename,
	// 		contentTypes.syllabus.elements.stages__stages.codename,
	// 		contentTypes.syllabus.elements.focus_areas.codename,
	// 		contentTypes.syllabus.elements.relatedlifeskillssyllabus.codename,
	// 		contentTypes.syllabus.elements.doredirect.codename,
	// 		contentTypes.syllabus.elements.allowpreview.codename,
	// 		contentTypes.syllabus.elements.courses.codename,
	// 		contentTypes.focusarea.elements.syllabus_type__items.codename,
	// 		contentTypes.focusarea.elements.stages__stages.codename,
	// 		contentTypes.focusarea.elements.stages__stage_years.codename,
	// 		contentTypes.focusarea.elements.syllabus.codename,
	// 		contentTypes.optionslist.elements.focus_area_options.codename,
	// 	],
	// 	allFilter: isAllowPreviewExternalSyllabus()
	// 		? null
	// 		: {
	// 				element: `elements.${contentTypes.syllabus.elements.doredirect.codename}`,
	// 				value: ['no'],
	// 		  },
	// })

	// const previewableSyllabusesOnly = syllabusResponse.items.filter(
	// 	filterPreviewableSyllabusesOnly,
	// )

	// const syllabusPathsByFocusAreaCodename = previewableSyllabusesOnly.reduce(
	// 	(acc, syllabus) => {
	// 		const { focus_areas } = syllabus.elements

	// 		let relatedLsSyllabuses =
	// 			syllabus.elements.relatedlifeskillssyllabus.value || []
	// 		if (!relatedLsSyllabuses.length) {
	// 			relatedLsSyllabuses = previewableSyllabusesOnly
	// 				.filter((syl) =>
	// 					syl.elements.relatedlifeskillssyllabus.value.includes(
	// 						syllabus.system.codename,
	// 					),
	// 				)
	// 				.map((syl) => syl.system.codename)
	// 		}

	// 		const syllabusPaths = [
	// 			syllabus.system.codename,
	// 			...relatedLsSyllabuses,
	// 		].map(getSlugByCodename)

	// 		focus_areas.value.forEach((fa) => {
	// 			acc[fa] = {
	// 				syllabusPaths,
	// 			}
	// 		})
	// 		return acc
	// 	},
	// 	{} as RecordFocusAreaPaths,
	// )

	// const focusAreasOrOptionslist = previewableSyllabusesOnly.flatMap(
	// 	(syllabus) => {
	// 		return getLinkedItems(
	// 			syllabus.elements.focus_areas,
	// 			syllabusResponse.linkedItems,
	// 		)
	// 	},
	// )

	// const paths = focusAreasOrOptionslist
	// 	.flatMap((focusAreaOrOptionslist) => {
	// 		const _isOptionList = isOptionList(focusAreaOrOptionslist)

	// 		// syllabus path for the focus area
	// 		const { syllabusPaths } =
	// 			syllabusPathsByFocusAreaCodename[
	// 				focusAreaOrOptionslist.system.codename
	// 			]

	// 		return syllabusPaths.flatMap((syllabusPath) => {
	// 			// syllabus for the focus area
	// 			const syllabus = syllabusResponse.items.find(
	// 				(_syllabus) =>
	// 					_syllabus.system.codename ===
	// 					getCodenameBySlug(syllabusPath),
	// 			)
	// 			if (!syllabus) return null

	// 			const isStage6Syl = isStage6Syllabus(syllabus)

	// 			let lifeSkillFaStages =
	// 				isFocusarea(focusAreaOrOptionslist) ||
	// 				isOptionList(focusAreaOrOptionslist)
	// 					? getLifeSkillStages(
	// 							focusAreaOrOptionslist,
	// 							syllabus,
	// 							syllabusResponse.linkedItems,
	// 					  )
	// 					: []

	// 			const stagesOrYears = uniquePrimitiveArray([
	// 				...(isStage6Syl
	// 					? focusAreaOrOptionslist.elements.stages__stage_years.value.flatMap(
	// 							byTaxoCodename,
	// 					  )
	// 					: focusAreaOrOptionslist.elements.stages__stages.value.flatMap(
	// 							byTaxoCodename,
	// 					  )),
	// 				...lifeSkillFaStages,
	// 			]).filter(
	// 				(stageOrYearCodename) => stageOrYearCodename != 'stage_6',
	// 			)

	// 			return stagesOrYears.filter(fnExist).flatMap((stageOrYear) => {
	// 				// if it's a life skill focus area

	// 				return syllabus.elements.key_learning_area__items.value.flatMap(
	// 					(keyLearningArea) => {
	// 						if (_isOptionList) {
	// 							return focusAreaOrOptionslist.elements.focus_area_options.value.map(
	// 								(optionCodename) => {
	// 									return {
	// 										params: {
	// 											learningarea: getSlugByCodename(
	// 												keyLearningArea.codename,
	// 											),
	// 											syllabus: syllabusPath,
	// 											tab: 'content',
	// 											stage: getSlugByCodename(
	// 												stageOrYear,
	// 											),
	// 											afterStageSlugs: [
	// 												focusAreaOrOptionslist
	// 													.system.codename,
	// 												optionCodename,
	// 											].map(getSlugByCodename),
	// 										},
	// 									}
	// 								},
	// 							)
	// 						}

	// 						return [
	// 							{
	// 								params: {
	// 									learningarea: getSlugByCodename(
	// 										keyLearningArea.codename,
	// 									),
	// 									syllabus: syllabusPath,
	// 									tab: 'content',
	// 									stage: getSlugByCodename(stageOrYear),
	// 									afterStageSlugs: [
	// 										getSlugByCodename(
	// 											focusAreaOrOptionslist.system
	// 												.codename,
	// 										),
	// 									],
	// 								},
	// 							},
	// 						]
	// 					},
	// 				)
	// 			})
	// 		})
	// 	})
	// 	.filter(fnExist)

	return {
		paths: [],
		fallback: 'blocking',
	}
}

export const getStageOrYearFocusAreas = (
	syllabuses: Syllabus[], //for stage 6 expect syllabus with related life skills syllabus
	stage: TaxoStageWithLifeSkill,
	year: TaxoStageYearWithLifeSkill | '',
	linkedItems: IContentItemsContainer, //for stage 6 expect syllabus with related life skills syllabus
	isStage6Syl = false,
	mainFocusArea?: Focusarea | Optionslist, //i.e. focus area page LHS
	isSyllabusCourse?: boolean,
) => {
	const isMainFocusAreaLifeSkill =
		isLifeSkillFocusAreaOrOptionListOrOutcome(mainFocusArea)

	return syllabuses
		?.flatMap((syl) => {
			const _focusAreas = getLinkedItems(
				syl.elements.focus_areas,
				linkedItems,
			)

			let filteredFocusAreas = _focusAreas
				.filter(fnExist)
				.filter((a) => a.elements.course.value.length > 0)

			return convertToFocusareasOrOptionListOrFocusareaoptionsExtended(
				filteredFocusAreas as Focusarea[],
				syl,
			)
		})
		.filter(fnExist)
		.filter((item) => {
			const isLifeSkillFocusArea =
				isLifeSkillFocusAreaOrOptionListOrOutcome(item)

			if (stage === 'life_skills' || year === 'life_skills') {
				return isLifeSkillFocusArea
			}

			if (isSyllabusCourse || isLifeSkillFocusArea) {
				return true
			}

			const isFocusAreaWithinStage = item.elements.stages__stages?.value
				.map(byTaxoCodename)
				.includes(stage)
			const isFocusAreaWithinYear =
				item.elements.stages__stage_years?.value
					.map(byTaxoCodename)
					.includes(year as TaxoStageYear)

			if (isStage6Syl && stage === 'stage_6' && year) {
				// if mainFocusArea specified and is life skill focus area, return only life skill focus area within year
				if (mainFocusArea && isMainFocusAreaLifeSkill) {
					return isFocusAreaWithinYear && isMainFocusAreaLifeSkill
				}
				return isFocusAreaWithinYear
			}
			// if mainFocusArea specified and is life skill focus area, return only life skill focus area within stage
			if (mainFocusArea && isMainFocusAreaLifeSkill) {
				return isFocusAreaWithinStage && isMainFocusAreaLifeSkill
			}
			return isFocusAreaWithinStage
		})
		.sort((a, b) => {
			const isLifeSkillA = isLifeSkillFocusAreaOrOptionListOrOutcome(a)
			const isLifeSkillB = isLifeSkillFocusAreaOrOptionListOrOutcome(b)

			// mainstream first then life skills
			if (isLifeSkillA && !isLifeSkillB) return 1
			if (!isLifeSkillA && isLifeSkillB) return -1
			return 0
		})
}

export const getStaticProps: GetStaticProps<
	ReturnTypeOfGetStaticProps,
	CommonSyllabusPathParams
> = async (context) => {
	const { params, preview } = context
	const {
		learningarea: learningAreaSlug,
		syllabus: syllabusSlug,
		stage: stageOrYearSlug,
		tab: tabSlug,
		afterStageSlugs,
	} = params
	const [focusAreaSlug] = afterStageSlugs

	const commonSyllabusData = await getCommonSyllabusData(context)

	// `getCommonSyllabusData` returns null when the syllabus could not be fetched from
	// Kontent. Degrade to a 404 rather than crashing the destructure below - see the
	// logged reason from `getItemByCodename`.
	if (!commonSyllabusData) {
		return redirectTo404(
			`/learning-areas/${[
				learningAreaSlug,
				syllabusSlug,
				tabSlug,
				stageOrYearSlug,
				...afterStageSlugs,
			].join('/')}`,
		)
	}

	const {
		config,
		keyLearningAreas,
		mappings,
		syllabusesResponse,
		syllabusResponse,
		stageGroups,
		stages,
		years,
		syllabus,
		assets,
		defaultFocusAreaUrls,
		disabledStages,
		websiteConfiguration,
		webLinkVideos,
		webLinkExternals,
	} = commonSyllabusData

	// redirect to 404 if the syllabus is not allowed to be (pre)viewed
	const redirectPropsIfSyllabusIsNotPublished =
		getStaticPropsForRedirectTo404ForNonPreviewedSyllabus(
			syllabus,
			`/404?path=/learning-areas/${[
				learningAreaSlug,
				syllabusSlug,
				tabSlug,
				stageOrYearSlug,
				...afterStageSlugs,
			].join('/')}`,
		)
	if (redirectPropsIfSyllabusIsNotPublished) {
		return redirectPropsIfSyllabusIsNotPublished
	}

	// Check if URL KLA matches canonical KLA
	// Redirect to homepage if using non-canonical KLA
	const canonicalKlaCodename =
		syllabus.elements.key_learning_area_default?.value?.[0]?.codename ||
		syllabus.elements.key_learning_area__items?.value?.[0]?.codename

	const urlKlaCodename = learningAreaSlug.replace(/-/g, '_')

	if (canonicalKlaCodename && urlKlaCodename !== canonicalKlaCodename) {
		return redirectTo404(
			`/learning-areas/${[
				learningAreaSlug,
				syllabusSlug,
				tabSlug,
				stageOrYearSlug,
				...afterStageSlugs,
			].join('/')}`,
		)
	}

	if (
		shouldHideTabBasedOnCecContentHide(
			syllabus,
			tabSlug as CustomSyllabusTab,
		)
	) {
		return {
			redirect: {
				destination: `/404?path=/learning-areas/${[
					learningAreaSlug,
					syllabusSlug,
					tabSlug,
					stageOrYearSlug,
					...afterStageSlugs,
				].join('/')}`,
				permanent: false,
			},
		}
	}

	const isLifeSkillsSyl = isLifeSkillSyllabus(syllabus)
	const isStage6Syl = isStage6Syllabus(syllabus)
	const hasLifeSkillRelatedSyl = hasLifeSkillRelatedSyllabus(syllabus)

	const focusareaCodename = getCodenameBySlug(focusAreaSlug as string)
	const { stageCodename, yearCodename } =
		getStageAndYearCodenamesFromStageOrYearSlug(
			syllabusResponse,
			stageOrYearSlug as TaxoStageWithLifeSkill,
		)
	let syllabusCodename = getCodenameBySlug(syllabusSlug)

	const focusAreaElements = uniquePrimitiveArray([
		// Focus area
		contentTypes.focusarea.elements.outcomes.codename,
		contentTypes.focusarea.elements.stages__stages.codename,
		contentTypes.focusarea.elements.stages__stage_years.codename,
		contentTypes.focusarea.elements.contentgroups.codename,
		contentTypes.focusarea.elements.accesspointgroups.codename,
		contentTypes.focusarea.elements.syllabus_type__items.codename,
		contentTypes.focusarea.elements.content.codename,
		contentTypes.focusarea.elements.teachingadvice.codename,
		contentTypes.focusarea.elements.title.codename,
		contentTypes.focusarea.elements.content_staged.codename,
		contentTypes.focusarea.elements.related_focusareas.codename,
		contentTypes.focusarea.elements.syllabus.codename,
		contentTypes.focusarea.elements.seo_description.codename,
		contentTypes.focusarea.elements.accesspointcontent.codename,
		contentTypes.focusarea.elements.course.codename,

		contentTypes.syllabus.elements.courses.codename,

		// Focus area - Outcomes
		contentTypes.outcome.elements.code.codename,
		contentTypes.outcome.elements.description.codename,
		contentTypes.outcome.elements.isoverarching.codename,

		// Focus area - Content groups
		contentTypes.contentgroup.elements.content_items.codename,
		contentTypes.contentgroup.elements.stages__stages.codename,
		contentTypes.contentgroup.elements.stages__stage_years.codename,
		contentTypes.contentgroup.elements.content_staged.codename,
		contentTypes.contentgroup.elements.title.codename,
		contentTypes.contentgroup.elements.content.codename,
		contentTypes.contentgroup.elements.chps_links.codename,

		// Focus area - Content groups - Content item
		contentTypes.contentitem.elements.including_statements.codename,
		contentTypes.contentitem.elements.learningprogression_tags__literacy
			.codename,
		contentTypes.contentitem.elements.learningprogression_tags__numeracy
			.codename,
		contentTypes.contentitem.elements.examples_lang.codename,
		contentTypes.contentitem.elements.pathway__pathway.codename,

		// Focus area - Access point groups
		contentTypes.accesscontentgroup.elements.title.codename,
		contentTypes.accesscontentgroup.elements.stages__stages.codename,
		contentTypes.accesscontentgroup.elements.stages__stage_years.codename,
		contentTypes.accesscontentgroup.elements.access_content_items.codename,

		// Focus area - Access point groups - Access content items
		contentTypes.accesscontentitem.elements.title.codename,
		contentTypes.accesscontentitem.elements.examples.codename,

		// Focus area - Teaching advice
		contentTypes.teachingadvice.elements.resource_containers.codename,
		contentTypes.resource_container.elements.asset.codename,
		contentTypes.resource_container.elements.syllabuswide.codename,
		contentTypes.resource_container.elements.syllabus_type.codename,
		contentTypes.resource_container.elements.resource_type.codename,
		contentTypes.resource_container.elements.asset_published.codename,
		contentTypes.resource_container.elements.file_name.codename,

		// Focus area - Resources other (Weblinkext | Weblinkvideo) for Related files section
		contentTypes.focusarea.elements.resources_other.codename,
		contentTypes.focusareaoption.elements.resources_other.codename,
		// Weblinkvideo elements for linked items population
		contentTypes.weblinkvideo.elements.title.codename,
		contentTypes.weblinkvideo.elements.video_url.codename,
		contentTypes.weblinkvideo.elements.syllabus.codename,
		contentTypes.weblinkvideo.elements.resource_type.codename,
		contentTypes.weblinkvideo.elements.stages__stages.codename,
		contentTypes.weblinkvideo.elements.stages__stage_years.codename,
		// Weblinkext elements for linked items population
		contentTypes.weblinkext.elements.title.codename,
		contentTypes.weblinkext.elements.link_url.codename,
		contentTypes.weblinkext.elements.syllabus.codename,
		contentTypes.weblinkext.elements.resource_type.codename,
		contentTypes.weblinkext.elements.stages__stages.codename,
		contentTypes.weblinkext.elements.stages__stage_years.codename,

		// Focus area - content richtext elements
		contentTypes.contentrichtext.elements.content.codename,
		contentTypes.contentrichtext.elements.stages.codename,
		contentTypes.contentrichtext.elements.stage_years.codename,

		// Focus area - Prescriptions (OC2-3562)
		contentTypes.focusarea.elements.prescriptions.codename,
		contentTypes.focusareaoption.elements.prescriptions.codename,

		// Prescription content type elements (full traversal for resolveList + rendering)
		contentTypes.prescriptions_list.elements.title.codename,
		contentTypes.prescriptions_list.elements.title_ext1.codename,
		contentTypes.prescriptions_list.elements.title_ext2.codename,
		contentTypes.prescriptions_list.elements.years.codename,
		contentTypes.prescriptions_list.elements.status.codename,
		contentTypes.prescriptions_list.elements.status_message.codename,

		contentTypes.prescription_group.elements.title.codename,
		contentTypes.prescription_group.elements.prescription_work.codename,
		contentTypes.prescription_group.elements.prescription_list.codename,
		contentTypes.prescription_group.elements.syllabus.codename,

		contentTypes.prescription_option.elements.title.codename,
		contentTypes.prescription_option.elements.content.codename,
		contentTypes.prescription_option.elements.prescriptions.codename,
		contentTypes.prescription_option.elements.prescription_list.codename,
		contentTypes.prescription_option.elements.syllabus.codename,

		contentTypes.prescription_work.elements.author.codename,
		contentTypes.prescription_work.elements.title.codename,
		contentTypes.prescription_work.elements.title_link.codename,
		contentTypes.prescription_work.elements.subtitles.codename,
		contentTypes.prescription_work.elements.publication.codename,
		contentTypes.prescription_work.elements.prescription_type.codename,

		contentTypes.prescription_work_bundle.elements.prescription_work
			.codename,

		contentTypes.prescription_study.elements.title.codename,
		contentTypes.prescription_study.elements.content.codename,

		contentTypes.course.elements.slug.codename,
		contentTypes.course.elements.display_mode.codename,
		contentTypes.course.elements.content_accordion_title.codename,

		// Options list
		contentTypes.optionslist.elements.focus_area_options.codename,

		// Web link focus area
		contentTypes.web_link_contentgroup.elements.item.codename,
		contentTypes.web_link_contentgroup.elements.stages__stage_years
			.codename,
		contentTypes.web_link_contentgroup.elements.stages__stages.codename,
		contentTypes.web_link_contentgroup.elements.syllabus.codename,
		contentTypes.web_link_contentgroup.elements.title.codename,

		// Content lang
		contentTypes.content_langexample.elements.language.codename,
		contentTypes.content_langexample.elements.content.codename,

		// for links_placeholder_cc & links_placeholder_overarching
		contentTypes.links_placeholder_cc.elements.links.codename,
		contentTypes.links_placeholder_overarching.elements.links.codename,

		// Add tinymce to elementsParameter (custom editor type)
		contentTypes.tinymce.elements._kontent_rtb.codename,
		contentTypes.tinymce.elements.formula.codename,
	])

	// if syllabus is stage 6 syllabus and has related life skills syllabus, use related life skills syllabus
	let [
		_focusAreaOrOptionListResponse,
		syllabusFocusAreaResponse,
		_syllabusResponse,
	] = await Promise.all([
		getItemByCodename<Focusarea | Optionslist>({
			codename: focusareaCodename,
			preview,
			depth: 3,
			elementsParameter: focusAreaElements,
		}),
		getAllItemsByType<Syllabus>({
			type: contentTypes.syllabus.codename,
			inFilter: {
				element: 'system.codename',
				value:
					isStage6Syl &&
					hasLifeSkillRelatedSyl &&
					stageOrYearSlug === 'life-skills'
						? syllabus.elements.relatedlifeskillssyllabus.value
						: [
								syllabusCodename,
								...syllabus.elements.relatedlifeskillssyllabus
									.value,
						  ],
			},
			preview,
			depth: 2,
			elementsParameter: uniquePrimitiveArray([
				// Syllabus
				contentTypes.syllabus.elements.focus_areas.codename,
				contentTypes.syllabus.elements.key_learning_area__items
					.codename,
				contentTypes.syllabus.elements.key_learning_area_default
					.codename,
				contentTypes.syllabus.elements.has_examples.codename,
				contentTypes.syllabus.elements.has_examples_in.codename,
				contentTypes.syllabus.elements.pathways.codename,
				...focusAreaElements,
			]),
		}),
		getItemByCodename<Syllabus>({
			codename: (syllabusSlug as string).replace(/-/g, '_'),
			preview,
			depth: 1,
			elementsParameter: getSyllabusElements(['content']),
		}),
	])

	if (!_focusAreaOrOptionListResponse?.item) {
		return {
			redirect: {
				destination: `/learning-area/${learningAreaSlug}/${syllabusSlug}/content/${stageOrYearSlug}`,
				permanent: false,
			},
		}
	}

	// OC2-7755: load the text-prescription tree separately, then merge it back.
	//
	// `focusarea.prescriptions` / `focusareaoption.prescriptions` point at
	// prescription_group and prescription_option items whose subtree is four levels deep:
	//   root -> prescription_work -> title_link weblink -> wp_custom_view
	//        -> resource_container
	//
	const readPrescriptionCodenames = (item: IContentItem): string[] =>
		(item?.elements as Focusarea['elements'])?.prescriptions?.value ?? []

	const prescriptionCodenames = uniquePrimitiveArray([
		...readPrescriptionCodenames(_focusAreaOrOptionListResponse.item),
		...Object.values(_focusAreaOrOptionListResponse.linkedItems)
			.filter(
				(linkedItem) =>
					linkedItem.system.type ===
						contentTypes.focusarea.codename ||
					linkedItem.system.type ===
						contentTypes.focusareaoption.codename,
			)
			.flatMap(readPrescriptionCodenames),
	])

	if (prescriptionCodenames.length) {
		const { items: prescriptions, linkedItems: prescriptionLinkedItems } =
			await getItemsByCodenames({
				codenames: prescriptionCodenames,
				// Depth 5 from the prescription root covers the whole tree with margin -
				// the deepest chain observed is (root -> work -> weblink ->
				// wp_custom_view -> resource_container).
				depth: 5,
				elementsParameter: focusAreaElements,
				preview,
			})

		_focusAreaOrOptionListResponse = {
			..._focusAreaOrOptionListResponse,
			linkedItems: {
				..._focusAreaOrOptionListResponse.linkedItems,
				...Object.fromEntries(
					prescriptions.map((prescription) => [
						prescription.system.codename,
						prescription,
					]),
				),
				...prescriptionLinkedItems,
			},
		}
	}

	// Parse linkedItemCodenames from all rich text fields and fetch missing items
	// This handles items referenced by <object> tags that are not automatically fetched
	// NOTE: elementsParameter and depth do NOT fetch items referenced by <object> tags!
	const allLinkedItemCodenames = new Set<string>()
	Object.values(_focusAreaOrOptionListResponse.linkedItems).forEach(
		(item: any) => {
			if (item.elements) {
				Object.values(item.elements).forEach((elem: any) => {
					if (
						elem.linkedItemCodenames &&
						Array.isArray(elem.linkedItemCodenames)
					) {
						elem.linkedItemCodenames.forEach((codename: string) => {
							allLinkedItemCodenames.add(codename)
						})
					}
				})
			}
		},
	)

	// Filter out already-fetched items
	const missingCodenames = Array.from(allLinkedItemCodenames).filter(
		(codename) => !_focusAreaOrOptionListResponse.linkedItems[codename],
	)

	// Fetch missing items
	if (missingCodenames.length > 0) {
		const fetchedItems = await Promise.all(
			missingCodenames.map((codename) =>
				getItemByCodename({
					codename,
					preview,
					depth: 0,
				}),
			),
		)

		// Merge into linkedItems
		fetchedItems.forEach((response) => {
			if (response?.item) {
				_focusAreaOrOptionListResponse.linkedItems[
					response.item.system.codename
				] = response.item
			}
		})
	}

	// check whether the focus area is matched with the syllabus and its syllabus related life skills syllabus
	const redirectPropsIfFocusAreaNotBelongToSyllabus =
		getRedirectPropsForFocusAreaNotMatchSyllabus(
			params,
			syllabus,
			{
				..._focusAreaOrOptionListResponse.linkedItems,
				...syllabusResponse.linkedItems,
			},
			`/404?path=/learning-areas/${[
				learningAreaSlug,
				syllabusSlug,
				tabSlug,
				stageOrYearSlug,
				...afterStageSlugs,
			].join('/')}`,
		)

	if (redirectPropsIfFocusAreaNotBelongToSyllabus) {
		return redirectPropsIfFocusAreaNotBelongToSyllabus
	}

	const focusAreaOrOptionList = _focusAreaOrOptionListResponse.item
	const _isFocusArea = isFocusarea(focusAreaOrOptionList)

	const relatedLifeSkillsSyllabuses =
		getLinkedItems(
			syllabus.elements.relatedlifeskillssyllabus,
			syllabusResponse.linkedItems,
		) || []

	const syllabusCourses = getLinkedItems(
		_syllabusResponse.item.elements.courses,
		_syllabusResponse.linkedItems,
	)

	const extensionSyllabusCourse = syllabusCourses.filter(
		(course) =>
			isYes(course.elements.display_mode) &&
			getSlugByCodenameYear(
				course.elements.stage_years.value[0].codename,
			) +
				'-' +
				course.elements.slug?.value ===
				params.stage,
	)

	const isExtensionCourse = !!(
		extensionSyllabusCourse && extensionSyllabusCourse.length
	)

	const stageFocusAreas =
		getStageOrYearFocusAreas(
			syllabusFocusAreaResponse.items,
			stageCodename,
			yearCodename,
			syllabusFocusAreaResponse.linkedItems,
			isStage6Syl,
			focusAreaOrOptionList,
			isExtensionCourse,
		) || []

	const focusAreaOutcomes = _isFocusArea
		? getLinkedItems(
				focusAreaOrOptionList.elements.outcomes,
				_focusAreaOrOptionListResponse.linkedItems,
		  )
		: []

	const stageOverarchingOutcomes = _isFocusArea
		? syllabusFocusAreaResponse.items.flatMap((focusArea) => {
				const outcomes = getLinkedItems(
					focusArea.elements.outcomes,
					syllabusFocusAreaResponse.linkedItems,
				)
				return outcomes
					.filter(isOutcome)
					.filter((outcome) => isYes(outcome.elements.isoverarching))
		  })
		: []

	const stageFocusAreaOutcomes = _isFocusArea
		? [
				...stageOverarchingOutcomes,
				...focusAreaOutcomes?.filter(isOutcome)?.filter((outcome) => {
					if (isYes(outcome.elements.isoverarching)) return true
					if (stageCodename === 'life_skills') {
						return isLifeSkillFocusAreaOrOptionListOrOutcome(
							outcome,
						)
					}
					return outcome.elements.stages__stages.value
						.map(byTaxoCodename)
						.includes(stageCodename)
				}),
		  ]
		: []

	const stageFocusAreaOutcomesCodenames = stageFocusAreaOutcomes.map(
		byIContentItemCodename,
	)

	const _props: InferGetStaticPropsType<typeof getStaticProps> = {
		mappings,
		preview: !!preview,
		previewData: null,
		params: {
			...params,
			slug: ['learning-areas', params.learningarea, params.syllabus],
		},
		data: {
			config,
			pageResponse: null,
			syllabuses: {
				...syllabusesResponse,
				items: syllabusesResponse.items.filter(
					filterPreviewableSyllabusesOnly,
				),
			},
			stages,
			years,
			stageGroups,
			keyLearningAreas,
			glossaries: null,
			assets: assets
				// .filter(
				// 	(asset) => !(isWebLinkVideo(asset) || isWebLinkext(asset)),
				// )
				.filter((asset) => {
					// if life skills
					if (stageCodename === 'life_skills') {
						/**
						 * Return only asset which has taxo syllabus intersect with
						 * related syllabus
						 */
						if (isLifeSkillsSyl) {
							return asset.elements.stages__stages.value.some(
								(s) => s.codename === 'stage_6',
							)
						}

						return relatedLifeSkillsSyllabuses?.length
							? isIntersect(
									relatedLifeSkillsSyllabuses.flatMap(
										(syllabus) =>
											syllabus.elements.syllabus.value.map(
												byTaxoCodename,
											),
									),
									asset.elements.syllabus.value.map(
										byTaxoCodename,
									),
							  )
							: true
					}

					const assetYears = getTaxoCodenamesFromTaxoTerms(
						asset.elements.stages__stage_years.value,
					)

					const currentPageYears = yearCodename
						? [yearCodename]
						: STAGE_YEARS[stageCodename]

					const formattedCurrentPageYears = currentPageYears?.map(
						(codename) => codename.split('_')[0] || codename,
					)

					return isIntersect(assetYears, formattedCurrentPageYears)
				}),
			stageFocusAreas,
			syllabus: _syllabusResponse,
			focusArea: {
				..._focusAreaOrOptionListResponse,
				item: {
					...focusAreaOrOptionList,
					elements: {
						...focusAreaOrOptionList.elements,
						outcomes:
							focusAreaOrOptionList.elements && _isFocusArea
								? {
										...focusAreaOrOptionList.elements
											.outcomes,
										value: stageFocusAreaOutcomesCodenames,
								  }
								: undefined,
						focus_area_options:
							focusAreaOrOptionList && !_isFocusArea
								? {
										...focusAreaOrOptionList.elements
											.focus_area_options,
								  }
								: undefined,
						key_learning_area__items:
							syllabus.elements.key_learning_area__items,
					},
				},
			} as unknown as Responses.IViewContentItemResponse<FocusareaOrOptionListOrFocusareoptionExtended>,
			defaultFocusAreaUrls,
			disabledStages,
			stageOrYears: getStagesOrYearsBasedOnSyllabusAndDisabledStages(
				stages,
				years,
				disabledStages,
				syllabusResponse.item,
			),
			syllabusCourses,
			syllabusFocusAreaResponse,
			websiteConfiguration,
			webLinkVideos: webLinkVideos?.items || [],
			webLinkExternals: webLinkExternals?.items || [],
		},
	}

	const cleanProps = cleanJson(_props)

	return {
		props: {
			rootLayoutClassName: 'max-w-none mx-0 px-0 !pt-0',
			...cleanProps,
		},
	}
}

export const getContentStageLayout: NextPageGetLayout = (
	page,
	serverRouter,
	pageProps: CommonPageProps<Syllabus, CommonContentTab<Syllabus>>,
) => {
	if (serverRouter.isFallback) return <>Fallback...</>

	const { params, data } = pageProps || {}
	const {
		stageOrYears,
		defaultFocusAreaUrls,
		syllabusCourses: syllabusCourseItems,
	} = data

	return getTabLayout(
		<StageOrYearContentAccordion
			params={params}
			stagesOrYears={stageOrYears}
			defaultFocusAreaPerStageOrYear={defaultFocusAreaUrls}
			syllabusCourseItems={syllabusCourseItems}
		>
			{page}
		</StageOrYearContentAccordion>,
		serverRouter,
		pageProps,
	)
}

const getLayout: NextPageGetLayout = (
	page,
	serverRouter,
	pageProps: ContentStageFocusAreaPageProps,
) => {
	if (serverRouter.isFallback) return <>Fallback...</>
	return getContentStageLayout(page, serverRouter, pageProps)
}

ContentStageFocusAreaPage.getLayout = getLayout
